import dayjs, { Dayjs } from "dayjs";
import { useState, useEffect } from "react";
import { RadioGroup } from "@mui/material";
import { FaUser, FaUsers } from "react-icons/fa";
import CustomRadioButton from "components/RadioButton";
import { dateToday } from "utils/dateToday";
import { Drawer } from "components/Drawer";
import { DateRangePicker } from "components/DateRangePicker";
import { TpMultiSelectAutocomplete } from "components/TpMultiSelectAutocomplete";
import { StyledToggleButtonGroup } from "components/StyledToggleButtonGroup";
import { FilterSection, RadioSection, InputSection, CustomDateSection, SectionTitle } from "./styles";

type DateRange<T> = [T | null, T | null];

interface IDashboardFilterProps {
  open: boolean;
  setOpen: (x: boolean) => void;
  onChangeParams: (x: any) => void;
  loading: boolean;
  initialParams?: {
    startDate?: string;
    endDate?: string;
    transshipPointsIds?: number[];
    groupBy?: string;
  };
}

const today = new Date();
const last7Days = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7);
const last15Days = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 15);
const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());

const formatDate = (date: Date) => dayjs(date).format("YYYY-MM-DD");

const initialEntries = [
  { id: 1, name: "Hoje", query: formatDate(today) },
  { id: 2, name: "Último mês", query: formatDate(lastMonth) },
  { id: 3, name: "Últimos 15 dias", query: formatDate(last15Days) },
  { id: 4, name: "Últimos 7 dias", query: formatDate(last7Days) },
  { id: 5, name: "Período customizado", query: "custom" },
];

const groupByOptions = [
  { value: "driver", label: "Motorista", icon: <FaUser size={14} /> },
  { value: "team", label: "Time", icon: <FaUsers size={18} /> },
];

export function FilterDashboard({ open, setOpen, onChangeParams, loading, initialParams }: IDashboardFilterProps) {
  // Determina qual período predefinido está sendo usado
  const getInitialSelectedDate = () => {
    if (!initialParams?.startDate || !initialParams?.endDate) {
      return formatDate(today); // Default para "Hoje"
    }

    const startDate = initialParams.startDate;
    const endDate = initialParams.endDate;
    const todayFormatted = formatDate(today);

    // Se é um período customizado (startDate != endDate e endDate != hoje)
    if (startDate !== endDate && endDate !== todayFormatted) {
      return "custom";
    }

    // Verifica se corresponde a algum período predefinido
    const matchingEntry = initialEntries.find(entry => entry.query === startDate);
    return matchingEntry ? matchingEntry.query : "custom";
  };

  const initialSelectedDate = getInitialSelectedDate();

  // Determina o dateRange inicial se for customizado
  const getInitialDateRange = (): DateRange<Dayjs> => {
    if (!initialParams?.startDate || !initialParams?.endDate || initialSelectedDate !== "custom") {
      return [null, null];
    }

    try {
      const startDayjs = dayjs(initialParams.startDate);
      const endDayjs = dayjs(initialParams.endDate);
      return [startDayjs, endDayjs];
    } catch {
      return [null, null];
    }
  };

  const [selectedDate, setSelectedDate] = useState<string>(initialSelectedDate);
  const [dateRange, setDateRange] = useState<DateRange<Dayjs>>(getInitialDateRange());
  const [selectedTransshipPoints, setSelectedTransshipPoints] = useState<number[]>(
    initialParams?.transshipPointsIds || []
  );
  const [selectedGroupBy, setSelectedGroupBy] = useState<string>(
    initialParams?.groupBy || "driver"
  );
  const [pendingParams, setPendingParams] = useState<any>(initialParams || {});

  // Função para determinar qual período predefinido está sendo usado
  const getUpdatedSelectedDate = () => {
    if (!initialParams?.startDate || !initialParams?.endDate) {
      return formatDate(today); // Default para "Hoje"
    }

    const startDate = initialParams.startDate;
    const endDate = initialParams.endDate;
    const todayFormatted = formatDate(today);

    // Se é um período customizado (startDate != endDate e endDate != hoje)
    if (startDate !== endDate && endDate !== todayFormatted) {
      return "custom";
    }

    // Verifica se corresponde a algum período predefinido
    const matchingEntry = initialEntries.find(entry => entry.query === startDate);
    return matchingEntry ? matchingEntry.query : "custom";
  };

  // Função para determinar o dateRange se for customizado
  const getUpdatedDateRange = (): DateRange<Dayjs> => {
    const newSelectedDate = getUpdatedSelectedDate();
    if (!initialParams?.startDate || !initialParams?.endDate || newSelectedDate !== "custom") {
      return [null, null];
    }

    try {
      const startDayjs = dayjs(initialParams.startDate);
      const endDayjs = dayjs(initialParams.endDate);
      return [startDayjs, endDayjs];
    } catch {
      return [null, null];
    }
  };

  // Função para sincronizar todos os estados internos
  const syncStatesFromParams = () => {
    if (!initialParams) return;

    const newSelectedDate = getUpdatedSelectedDate();
    setSelectedDate(newSelectedDate);
    setDateRange(getUpdatedDateRange());
    setSelectedTransshipPoints(initialParams.transshipPointsIds || []);
    setSelectedGroupBy(initialParams.groupBy || "driver");
    setPendingParams(initialParams);
  };

  // Sincroniza estados internos quando initialParams mudam
  useEffect(() => {
    syncStatesFromParams();
  }, [initialParams?.startDate, initialParams?.endDate, initialParams?.transshipPointsIds, initialParams?.groupBy]);

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = event.target.value;
    setSelectedDate(newDate);

    if (newDate !== "custom") {
      setDateRange([null, null]);
      const todayDate = formatDate(today);
      setPendingParams((prev: any) => ({
        ...prev,
        startDate: newDate,
        endDate: todayDate // endDate sempre é hoje para períodos predefinidos
      }));
    } else {
      // Para período customizado, limpa as datas predefinidas
      setPendingParams((prev: any) => {
        const { startDate, endDate, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleCloseDrawer = () => {
    setOpen(false);
  };

  const handleClearFilters = () => {
    const todayFormatted = formatDate(today);
    setSelectedDate(todayFormatted); // Volta para "Hoje"
    setDateRange([null, null]);
    setSelectedTransshipPoints([]);
    setSelectedGroupBy("driver"); // Volta para "Motorista"
    setPendingParams({
      startDate: todayFormatted,
      endDate: todayFormatted,
      transshipPointsIds: [],
      groupBy: "driver"
    });
  };

  const handleApplyFilters = () => {
    if (Object.keys(pendingParams).length > 0) {
      onChangeParams(pendingParams);
    }
    handleCloseDrawer();
  };

  const handleDateRangeChange = (newDateRange: DateRange<Dayjs>) => {
    setDateRange(newDateRange);

    if (newDateRange[0] && newDateRange[1]) {
      setPendingParams((prev: any) => ({
        ...prev,
        startDate: newDateRange[0]!.format('YYYY-MM-DD'),
        endDate: newDateRange[1]!.format('YYYY-MM-DD')
      }));
    }
  };

  const handleTransshipPointsChange = (selectedIds: number[]) => {
    setSelectedTransshipPoints(selectedIds);
    setPendingParams((prev: any) => ({ ...prev, transshipPointsIds: selectedIds }));
  };

  const handleGroupByChange = (newGroupBy: string) => {
    setSelectedGroupBy(newGroupBy);
    setPendingParams((prev: any) => ({ ...prev, groupBy: newGroupBy }));
  };

  return (
    <Drawer
      open={open}
      onClose={handleCloseDrawer}
      title="Filtros"
      onClear={handleClearFilters}
      onApply={handleApplyFilters}
      clearButtonText="Limpar filtros"
      applyButtonText="Aplicar"
      buttonsDisabled={loading}
    >
      <RadioSection>
        <SectionTitle>Período</SectionTitle>
        <RadioGroup value={selectedDate} onChange={handleRadioChange}>
          {initialEntries.map((item) => (
            <CustomRadioButton
              key={item.id}
              text={item.name}
              value={item.query}
              checked={selectedDate === item.query}
              handleChange={handleRadioChange}
              disabled={loading}
            />
          ))}
        </RadioGroup>
      </RadioSection>

      {selectedDate === "custom" && (
        <CustomDateSection>
          <DateRangePicker
            value={dateRange}
            onChange={handleDateRangeChange}
            label="Selecione o período"
            disabled={loading}
            placeholder="Escolha as datas"
            maxDays={30}
          />
        </CustomDateSection>
      )}

      <InputSection>
        <SectionTitle>Centros de Distribuição</SectionTitle>
        <TpMultiSelectAutocomplete
          value={selectedTransshipPoints}
          onChange={handleTransshipPointsChange}
          label="Selecione os CDs"
          disabled={loading}
          placeholder="Escolha os centros de distribuição"
          loading={loading}
        />
      </InputSection>

      <InputSection>
        <SectionTitle>Visualização</SectionTitle>
        <StyledToggleButtonGroup
          value={selectedGroupBy}
          onChange={(_, newValue) => newValue && handleGroupByChange(newValue)}
          disabled={loading}
          options={groupByOptions}
        />
      </InputSection>
    </Drawer>
  );
}
